import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import { useGameStore } from '../store/gameStore'
import { db } from '../lib/supabase'
import { stripe } from '../lib/stripe'
import Button from '../components/Button'
import { User, Mail, CreditCard, LogOut } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n/I18nProvider'

function Account() {
  const { t } = useI18n()
  const user = useAuthStore((state) => state.user)
  const signOut = useAuthStore((state) => state.signOut)
  const [subscriptionData, setSubscriptionData] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    loadSubscriptionData()
  }, [user])

  const loadSubscriptionData = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      const data = await db.getSubscription(user.id)
      setSubscriptionData(data)
    } catch (error) {
      console.error('Failed to load subscription:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleManageSubscription = async () => {
    try {
      await stripe.createPortalSession(user.id)
    } catch (error) {
      console.error('Failed to open portal:', error)
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-600"></div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-green-800 mb-8 text-center">
          {t('account.title')}
        </h1>

        <div className="space-y-6">
          {/* User Info */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {t('account.personalInfoTitle')}
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <User size={24} className="text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">{t('account.usernameLabel')}</p>
                  <p className="font-bold text-gray-900">
                    {user?.user_metadata?.username || t('account.usernameFallback')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Mail size={24} className="text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">{t('account.emailLabel')}</p>
                  <p className="font-bold text-gray-900">{user?.email}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                className="w-full"
              >
                {t('account.editProfile')}
              </Button>
            </div>
          </div>

          {/* Subscription Info */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <CreditCard size={24} />
              {t('account.subscriptionTitle')}
            </h2>

            {subscriptionData && subscriptionData.status === 'active' ? (
              <div>
                <div className="bg-green-100 border border-green-300 rounded-lg p-4 mb-4">
                  <p className="font-bold text-green-800 mb-2">
                    {t('account.subscriptionActive')}
                  </p>
                  <p className="text-sm text-green-700">
                    {t('account.subscriptionRenew')}{' '}
                    {new Date(subscriptionData.current_period_end).toLocaleDateString(t('account.localeCode') || 'fr-FR')}
                  </p>
                </div>

                <Button
                  onClick={handleManageSubscription}
                  variant="outline"
                  className="w-full"
                >
                  {t('account.manageSubscription')}
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-gray-600 mb-4">
                  {t('account.noSubscription')}
                </p>
                <Button
                  onClick={() => navigate('/shop')}
                  className="w-full"
                >
                  {t('account.viewPlans')}
                </Button>
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-red-200">
            <h2 className="text-2xl font-bold text-red-600 mb-6">
              {t('account.dangerZoneTitle')}
            </h2>
            
            <Button
              onClick={handleSignOut}
              variant="danger"
              className="w-full flex items-center justify-center gap-2"
            >
              <LogOut size={20} />
              {t('account.signOut')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Account
